import { useMemo, type ReactNode } from 'react';

import type { MenuItem } from '@/consts/navigation';
import { MAX_SEARCH_RESULTS } from '@/features/header/consts/search';
import { buildResults, flattenLeafItems, getFilteredChildren } from '@/features/header/utils/search';
import { useDynamicMenu } from '@/features/navigation/hooks/useDynamicMenu';
import { normalizeString } from '@/utils/string';
import { Compass } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface SearchResult {
  to: string;
  category: string;
  label: string;
  icon: ReactNode;
}

const DYNAMIC_SECTIONS = new Set(['navigation.favorites.title', 'navigation.recent.title']);

const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const useSearchResults = (query: string): SearchResult[] => {
  const { t, i18n } = useTranslation();
  const { menu: dynamicMenu } = useDynamicMenu();

  const searchableMenu = useMemo(() => dynamicMenu.filter((item) => !DYNAMIC_SECTIONS.has(item.text)), [dynamicMenu]);

  return useMemo(() => {
    const lowerQuery = normalizeString(query.trim());
    if (!lowerQuery) return [];

    const regex = new RegExp(`\\b${escapeRegex(lowerQuery)}\\w*`, 'i');

    const searchSection = (section: MenuItem, fallbackIcon: React.ReactNode): SearchResult[] => {
      const sectionLabel = t(section.text);
      const sectionIcon = section.icon ?? fallbackIcon;

      if (regex.test(normalizeString(sectionLabel))) {
        return buildResults(sectionLabel, sectionIcon, flattenLeafItems(section.children));
      }

      return buildResults(sectionLabel, sectionIcon, getFilteredChildren(section, regex));
    };

    return searchableMenu
      .flatMap((menuItem) => {
        if (!menuItem.children) return [];

        const menuItemIcon = menuItem.icon ?? <Compass className="size-4" />;

        if (menuItem.isGroup) {
          const groupLabel = t(menuItem.text);
          const sections = menuItem.children.filter((child) => child.children);
          const leaves = menuItem.children.filter(
            (child) =>
              !child.hidden &&
              !child.children &&
              typeof child.to === 'string' &&
              regex.test(normalizeString(t(child.text)))
          );
          return [
            ...buildResults(groupLabel, menuItemIcon, leaves),
            ...sections.flatMap((section) => searchSection(section, menuItemIcon))
          ];
        }

        return searchSection(menuItem, menuItemIcon);
      })
      .slice(0, MAX_SEARCH_RESULTS);
  }, [query, i18n.language, searchableMenu]);
};
